import React, { useState, useEffect } from 'react';

const API_URL = 'https://tormal-api.winter-mouse-2fc0.workers.dev';

function TempMail() {
  const [email, setEmail] = useState('');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600);

  const generateEmail = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/temp-mail/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ duration_minutes: 10 })
      });
      const data = await res.json();
      if (data.email) {
        setEmail(data.email);
        setTimeLeft(600);
        checkInbox(data.email);
      }
    } catch (e) {
      // Fallback demo mode
      const random = Math.random().toString(36).substring(2, 10);
      setEmail(`${random}@tempmail.tormal.com`);
      setTimeLeft(600);
    }
    setLoading(false);
  };

  const checkInbox = async (emailAddress) => {
    try {
      const res = await fetch(`${API_URL}/api/temp-mail/inbox?email=${emailAddress}`);
      const data = await res.json();
      setEmails(data.emails || []);
    } catch (e) {
      // Demo mode - show sample emails
    }
  };

  useEffect(() => {
    if (!email) return;
    const interval = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          setEmail('');
          setEmails([]);
          return 0;
        }
        return t - 1;
      });
      checkInbox(email);
    }, 1000);
    return () => clearInterval(interval);
  }, [email]);

  const copyEmail = () => {
    navigator.clipboard.writeText(email);
    alert('Email copied to clipboard!');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Temporary Email</h1>
      <p style={styles.subtitle}>Get a disposable email address that expires in 10 minutes</p>

      {!email ? (
        <button style={styles.generateBtn} onClick={generateEmail} disabled={loading}>
          {loading ? 'Generating...' : '🎲 Generate Email Address'}
        </button>
      ) : (
        <div style={styles.emailBox}>
          <div style={styles.emailDisplay}>
            <span style={styles.emailText}>{email}</span>
            <button style={styles.copyBtn} onClick={copyEmail}>📋 Copy</button>
          </div>
          <div style={styles.timer}>⏱️ Expires in: {formatTime(timeLeft)}</div>
          <button style={styles.refreshBtn} onClick={() => checkInbox(email)}>
            🔄 Refresh Inbox
          </button>
        </div>
      )}

      {emails.length > 0 && (
        <div style={styles.inbox}>
          <h3 style={styles.inboxTitle}>📥 Inbox ({emails.length})</h3>
          {emails.map((msg, i) => (
            <div key={i} style={styles.emailItem}>
              <div style={styles.emailHeader}>
                <strong>From:</strong> {msg.from}
              </div>
              <div style={styles.emailSubject}>
                <strong>Subject:</strong> {msg.subject}
              </div>
              <div style={styles.emailBody}>{msg.body}</div>
            </div>
          ))}
        </div>
      )}

      <div style={styles.tips}>
        <h3>💡 Tips:</h3>
        <ul>
          <li>Use this email for sign-ups, trials, or anywhere you need temporary access</li>
          <li>All emails are automatically deleted after 10 minutes</li>
          <li>No registration required - completely anonymous</li>
        </ul>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '2rem',
  },
  title: {
    textAlign: 'center',
    fontSize: '2.5rem',
    marginBottom: '1rem',
  },
  subtitle: {
    textAlign: 'center',
    color: '#888',
    marginBottom: '3rem',
  },
  generateBtn: {
    display: 'block',
    width: '100%',
    maxWidth: '400px',
    margin: '0 auto',
    padding: '1.5rem 2rem',
    fontSize: '1.3rem',
    background: 'linear-gradient(135deg, #00d4ff, #7b2cbf)',
    color: '#fff',
    border: 'none',
    borderRadius: '16px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  emailBox: {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '16px',
    padding: '2rem',
    textAlign: 'center',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  emailDisplay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  emailText: {
    fontSize: '1.5rem',
    fontFamily: 'monospace',
    background: 'rgba(0,0,0,0.3)',
    padding: '1rem 1.5rem',
    borderRadius: '8px',
    wordBreak: 'break-all',
  },
  copyBtn: {
    padding: '0.8rem 1.5rem',
    background: '#2d2d44',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  timer: {
    fontSize: '1.2rem',
    color: '#ff6b6b',
    marginBottom: '1rem',
  },
  refreshBtn: {
    padding: '0.8rem 1.5rem',
    background: 'transparent',
    color: '#00d4ff',
    border: '1px solid #00d4ff',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  inbox: {
    marginTop: '2rem',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  inboxTitle: {
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  emailItem: {
    background: 'rgba(255,255,255,0.05)',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  emailHeader: {
    marginBottom: '0.5rem',
    color: '#888',
  },
  emailSubject: {
    marginBottom: '0.5rem',
    fontSize: '1.1rem',
  },
  emailBody: {
    color: '#aaa',
    fontSize: '0.9rem',
  },
  tips: {
    marginTop: '3rem',
    padding: '1.5rem',
    background: 'rgba(0,212,255,0.05)',
    borderRadius: '12px',
    border: '1px solid rgba(0,212,255,0.2)',
  },
};

export default TempMail;

